import type { Profile } from './profiles';

/**
 * Manager profile with specific client and project details
 */
export const managerProfile: Profile = {
  name: 'Manager',
  minMeetingsPerDay: 2,
  averageSwitchingFrequencySeconds: 240, // Switch context every ~4 minutes on average
  meetings: [
    {
      title: 'Quarterly Business Review',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - H1 2024.pptx' },
        { tool: 'microsoft_excel', title: 'Client Satisfaction Metrics - June 2024.xlsx' },
        { tool: 'notion', title: 'Acme Corp QBR Notes' },
        { tool: 'slack', title: 'client-acme' },
      ],
      aiNotes: {
        concise: 'Reviewing quarterly business performance with client.',
        detailed:
          'Presenting client success stories and satisfaction metrics from H1 2024, documenting meeting notes for Acme Corp quarterly business review, and communicating with client team through Slack.',
      },
    },
    {
      title: 'TechDynamics - Project Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Project Timelines - Q3 2024.xlsx' },
        { tool: 'notion', title: 'TechDynamics Project Requirements' },
        { tool: 'jira', title: 'TD-198: TechDynamics - API Integration Phase 2' },
        { tool: 'slack', title: 'project-techdynamics' },
      ],
      aiNotes: {
        concise: 'Planning project timelines and requirements for client.',
        detailed:
          'Reviewing project timelines for Q3 2024, discussing TechDynamics project requirements, planning API Integration Phase 2, and coordinating with project team through Slack.',
      },
    },
    {
      title: 'GlobalHealth - Implementation Kickoff',
      durationMinutes: 120,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Implementation Process Overview.pptx' },
        { tool: 'notion', title: 'GlobalHealth Implementation Plan' },
        { tool: 'jira', title: 'GH-421: GlobalHealth - User Provisioning System' },
        { tool: 'slack', title: 'project-globalhealth' },
      ],
      aiNotes: {
        concise: 'Kicking off implementation project with new client.',
        detailed:
          'Conducting implementation kickoff meeting for GlobalHealth, presenting overview of implementation process, discussing user provisioning system requirements, and communicating with project team on Slack.',
      },
    },
    {
      title: 'Daily Team Standup',
      durationMinutes: 15,
      entries: [
        { tool: 'notion', title: 'Daily Standup Notes' },
        { tool: 'jira', title: 'Sprint Board' },
        { tool: 'slack', title: 'leadership-team' },
      ],
      aiNotes: {
        concise: 'Daily team status update and coordination meeting.',
        detailed:
          'Facilitating daily standup meeting with leadership team, reviewing sprint board tasks in Jira, taking notes, and discussing team priorities.',
      },
    },
    {
      title: '1:1 with Sarah (Design Lead)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Performance Review Notes: Design Team' },
        { tool: 'microsoft_excel', title: 'Design Team Metrics - Q2 2024.xlsx' },
        { tool: 'slack', title: 'Sarah (DM)' },
      ],
      aiNotes: {
        concise: 'Reviewing design team performance metrics.',
        detailed:
          'Meeting with Design Lead to discuss design team performance metrics, reviewing Q2 data, preparing for upcoming performance reviews, and exchanging feedback through direct messages.',
      },
    },
    {
      title: '1:1 with Michael (Engineering Manager)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Engineering Roadmap Discussion' },
        { tool: 'jira', title: 'Engineering Team Sprint Dashboard' },
        { tool: 'slack', title: 'Michael (DM)' },
      ],
      aiNotes: {
        concise: 'Planning engineering team roadmap and sprint priorities.',
        detailed:
          'One-on-one meeting with Engineering Manager to discuss engineering roadmap, review current sprint dashboard progress, and coordinate on upcoming development priorities through direct messages.',
      },
    },
    {
      title: 'Department All-Hands',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'notion', title: 'Team OKRs Q2 2024' },
        { tool: 'slack', title: 'all-hands-announcements' },
      ],
      aiNotes: {
        concise: 'Presenting department performance and reviewing quarterly objectives.',
        detailed:
          'Leading department all-hands meeting, presenting Q2 performance results, discussing team OKRs achievement status, and sharing important announcements with the entire department.',
      },
    },
    {
      title: 'Executive Team Meeting',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Executive Updates - Q2 2024.pptx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'notion', title: 'Executive Team Agenda' },
        { tool: 'slack', title: 'executive-team' },
      ],
      aiNotes: {
        concise: 'Discussing quarterly results and revenue forecasts with executive team.',
        detailed:
          'Participating in executive team meeting, reviewing Q2 performance updates, analyzing H2 revenue forecasts, following the structured agenda, and coordinating with leadership through executive Slack channel.',
      },
    },
    {
      title: 'Board Meeting Prep',
      durationMinutes: 60,
      entries: [
        { tool: 'keynote', title: 'Board Meeting Presentation - July 2024' },
        { tool: 'microsoft_excel', title: 'Financial Summary - Board Report.xlsx' },
        { tool: 'notion', title: 'Board Meeting Agenda' },
      ],
      aiNotes: {
        concise: 'Preparing presentation materials for upcoming board meeting.',
        detailed:
          'Creating board meeting presentation slides, organizing financial summary data for the board report, and finalizing the meeting agenda for the upcoming July board meeting.',
      },
    },
  ],
  sessions: [
    {
      name: 'Project Planning',
      predominantTool: 'jira',
      entries: [
        { tool: 'jira', title: 'ACM-342: Dashboard Implementation' },
        { tool: 'jira', title: 'TD-198: TechDynamics - API Integration Phase 2' },
        { tool: 'jira', title: 'GH-421: GlobalHealth - User Provisioning System' },
        { tool: 'notion', title: 'Project Plan: Acme Corp Q3-Q4 Roadmap' },
        { tool: 'notion', title: 'Resource Allocation: Q3 2024' },
        { tool: 'microsoft_excel', title: 'Project Timelines - Q3 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Resource Allocation Model.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Services Budget 2024.xlsx' },
        { url: 'https://miro.com', title: 'Miro - Strategic planning workshop' },
      ],
      aiNotes: {
        concise: 'Planning client project timelines and resource allocation.',
        detailed:
          'Managing client projects in Jira for Acme Corp, TechDynamics, and GlobalHealth, creating Q3-Q4 roadmaps, allocating resources, developing project timelines, budgeting for client services, and participating in strategic planning workshops.',
      },
    },
    {
      name: 'Report Preparation',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Q2 2024 - Department Performance.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Satisfaction Metrics - June 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Executive Presentation - Q2 Results.pptx' },
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - H1 2024.pptx' },
        { tool: 'microsoft_word', title: 'Department Strategy Document - 2024.docx' },
        { tool: 'keynote', title: 'Board Meeting Presentation - July 2024' },
        { url: 'https://tableau.com', title: 'Tableau - Department performance dashboard' },
        { url: 'https://slideshare.net', title: 'SlideShare - Industry benchmark data' },
      ],
      aiNotes: {
        concise: 'Creating performance reports and presentations for stakeholders.',
        detailed:
          'Analyzing department performance data, client satisfaction metrics, and revenue forecasts. Creating executive presentations showcasing Q2 results, client success stories, and preparing board meeting materials with industry benchmark comparisons.',
      },
    },
    {
      name: 'Communication',
      predominantTool: 'slack',
      entries: [
        { tool: 'slack', title: 'leadership-team' },
        { tool: 'slack', title: 'client-success' },
        { tool: 'slack', title: 'project-acme' },
        { tool: 'slack', title: 'Jennifer (DM)' },
        { tool: 'slack', title: 'David (DM)' },
        { tool: 'gmail', title: 'Email to Acme Corp: Quarterly Review Follow-up' },
        { tool: 'gmail', title: 'Email from GlobalHealth: Implementation Questions' },
        { tool: 'gmail', title: 'Email thread: Executive Team - Budget Approval' },
        { tool: 'gmail', title: 'Email thread: Department All-Hands Agenda' },
      ],
      aiNotes: {
        concise: 'Coordinating with leadership team and clients through various channels.',
        detailed:
          'Communicating through leadership and client-specific Slack channels, direct messaging team members, following up on Acme Corp quarterly review, addressing GlobalHealth implementation questions, and coordinating budget approvals and department meetings.',
      },
    },
    {
      name: 'Strategic Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Strategic Plan: 2024-2025' },
        { tool: 'notion', title: 'Competitive Analysis: Market Positioning' },
        { tool: 'notion', title: 'Product Roadmap: 18-Month Vision' },
        { tool: 'microsoft_excel', title: 'Growth Scenarios - 2024-2026.xlsx' },
        { tool: 'microsoft_excel', title: 'Department Hiring Plan.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Vision Presentation - Leadership Offsite.pptx' },
        { tool: 'jira', title: 'STRAT-42: Define Q3-Q4 Strategic Initiatives' },
        { url: 'https://hbr.org', title: 'Harvard Business Review - Team productivity article' },
        { url: 'https://gartner.com', title: 'Gartner - Industry trends report 2024' },
      ],
      aiNotes: {
        concise: 'Developing long-term strategy and competitive positioning.',
        detailed:
          'Creating strategic plan documentation, analyzing market positioning against competitors, mapping 18-month product vision, modeling growth scenarios, planning department hiring, preparing vision presentations for leadership offsite, and researching industry trends.',
      },
    },
    {
      name: 'Market Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://linkedin.com', title: 'LinkedIn - Industry updates for client meeting' },
        { url: 'https://wsj.com', title: 'Wall Street Journal - Tech sector outlook' },
        { url: 'https://gartner.com', title: 'Gartner - SaaS market forecast 2024-2026' },
        { url: 'https://google.com', title: 'Google search: Client retention strategies SaaS' },
        { tool: 'notion', title: 'Market Research: Competitive Analysis Q3 2024' },
      ],
      aiNotes: {
        concise: 'Researching industry trends and market forecasts.',
        detailed:
          'Gathering industry updates from LinkedIn for client meetings, reviewing tech sector outlook in Wall Street Journal, analyzing SaaS market forecasts, researching client retention strategies, and documenting competitive analysis findings.',
      },
    },
    {
      name: 'Client Relationship Management',
      predominantTool: 'salesforce',
      entries: [
        { url: 'https://salesforce.com', title: 'Salesforce - Client opportunity pipeline' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer satisfaction metrics' },
        { url: 'https://salesforce.com', title: 'Salesforce - Account renewal forecasting' },
        { url: 'https://hubspot.com', title: 'HubSpot - Customer engagement analytics' },
        { tool: 'microsoft_excel', title: 'Client Success Analysis - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Retention Analysis - June 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'Client Success Stories - Executive Review.pptx' },
        { tool: 'notion', title: 'Client Success Framework: 2024 Strategy' },
        { tool: 'notion', title: 'Key Account Management Playbook' },
        { tool: 'slack', title: 'client-success' },
        { tool: 'gmail', title: 'Email: Client Relationship Quarterly Review' },
      ],
      aiNotes: {
        concise: 'Managing client relationships and analyzing satisfaction metrics.',
        detailed:
          'Tracking client opportunity pipeline in Salesforce, monitoring customer satisfaction metrics, forecasting account renewals, reviewing engagement analytics, analyzing client success and retention metrics, and documenting client success frameworks and account management strategies.',
      },
    },
    {
      name: 'Performance Reviews',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Performance Review Template - Q2 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Performance Metrics - Q2 2024.xlsx' },
        { tool: 'notion', title: 'Performance Review Guidelines - Managers' },
        { tool: 'slack', title: 'hr-performance-reviews' },
      ],
      aiNotes: {
        concise: 'Preparing team performance evaluations for quarterly reviews.',
        detailed:
          'Working with performance review templates, analyzing team performance metrics for Q2, reviewing manager guidelines for conducting evaluations, and coordinating with HR team through dedicated Slack channel.',
      },
    },
    {
      name: 'Budget Planning',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Q3 2024 - Department Budget Planning.xlsx' },
        { tool: 'microsoft_excel', title: 'Budget Forecast - 2024-2025.xlsx' },
        { tool: 'microsoft_excel', title: 'Expense Allocation - Q3 2024.xlsx' },
        { tool: 'notion', title: 'Budget Planning Process - 2024' },
      ],
      aiNotes: {
        concise: 'Planning department budget allocations and forecasts.',
        detailed:
          'Developing Q3 department budget plans, creating annual budget forecasts for 2024-2025, allocating expenses for the upcoming quarter, and documenting budget planning processes and guidelines.',
      },
    },
    {
      name: 'Team Hiring',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Job Description - Senior Developer' },
        { tool: 'notion', title: 'Hiring Process - Technical Roles' },
        { tool: 'gmail', title: 'Candidate Resume Review Thread' },
        { tool: 'microsoft_excel', title: 'Candidate Evaluation Matrix.xlsx' },
      ],
      aiNotes: {
        concise: 'Managing technical hiring process and candidate evaluations.',
        detailed:
          'Creating job descriptions for senior developer position, documenting technical role hiring processes, reviewing candidate resumes via email thread, and tracking candidate evaluations using structured evaluation matrix.',
      },
    },
    {
      name: 'Process Improvement Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Process Improvement Initiative - Q3 2024' },
        { tool: 'notion', title: 'Team Workflow Optimization Plan' },
        { tool: 'miro', title: 'Process Flow Mapping Workshop' },
        { tool: 'linear', title: 'OKR-12: Improve team delivery efficiency' },
      ],
      aiNotes: {
        concise: 'Planning workflow optimizations to improve team efficiency.',
        detailed:
          'Developing process improvement initiatives for Q3, planning team workflow optimizations, facilitating process flow mapping workshops, and tracking team delivery efficiency objectives and key results.',
      },
    },
    {
      name: 'Client Proposal Development',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Proposal - MegaCorp Expansion.docx' },
        { tool: 'microsoft_excel', title: 'Project Cost Estimation - MegaCorp.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'MegaCorp Proposal - Presentation Deck.pptx' },
        { tool: 'notion', title: 'MegaCorp Client Requirements' },
      ],
      aiNotes: {
        concise: 'Creating expansion proposal and cost estimates for major client.',
        detailed:
          'Drafting client proposal document for MegaCorp expansion project, estimating project costs, developing presentation slides for proposal meeting, and documenting detailed client requirements.',
      },
    },
    {
      name: 'OKR Planning',
      predominantTool: 'notion',
      entries: [
        { tool: 'notion', title: 'Q3-Q4 OKRs - Department Planning' },
        { tool: 'notion', title: 'OKR Alignment - Cross-Functional Teams' },
        { tool: 'miro', title: 'OKR Planning Workshop Board' },
        { tool: 'microsoft_excel', title: 'OKR Tracking Template - 2024.xlsx' },
      ],
      aiNotes: {
        concise: 'Establishing objectives and key results for upcoming quarters.',
        detailed:
          'Planning department OKRs for Q3-Q4, aligning objectives across cross-functional teams, facilitating collaborative OKR planning workshops, and developing tracking templates for measuring progress.',
      },
    },
    {
      name: 'Vendor Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Vendor Comparison Matrix - 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Vendor Agreement - Software Solutions Inc.docx' },
        { tool: 'microsoft_excel', title: 'Vendor Performance Tracking.xlsx' },
        { tool: 'gmail', title: 'Vendor Negotiation Thread - Software Solutions' },
      ],
      aiNotes: {
        concise: 'Evaluating vendor options and negotiating service agreements.',
        detailed:
          'Comparing potential vendors using evaluation matrices, reviewing vendor agreement terms with Software Solutions Inc., tracking vendor performance metrics, and conducting contract negotiations via email.',
      },
    },
    {
      name: 'Remote Work Policy Development',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Remote Work Policy - 2024 Update.docx' },
        { tool: 'notion', title: 'Remote Work Guidelines for Managers' },
        { tool: 'microsoft_powerpoint', title: 'Remote Work Policy - Team Presentation.pptx' },
        { tool: 'slack', title: 'policy-development' },
      ],
      aiNotes: {
        concise: 'Updating remote work policies and management guidelines.',
        detailed:
          'Revising remote work policy documentation for 2024, creating guidelines for managers supervising remote teams, preparing presentation slides for policy rollout, and discussing policy updates with development team via Slack.',
      },
    },
    {
      name: 'Risk Assessment',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Project Risk Assessment - Q3 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Risk Mitigation Strategies.docx' },
        { tool: 'notion', title: 'Risk Management Framework - 2024' },
        { tool: 'slack', title: 'risk-compliance' },
      ],
      aiNotes: {
        concise: 'Identifying project risks and developing mitigation strategies.',
        detailed:
          'Conducting project risk assessments for Q3, documenting risk mitigation strategies, updating the risk management framework, and coordinating with compliance team through dedicated Slack channel.',
      },
    },
  ],
};
